import { FlatKeyNotes, SharpKeyNotes } from 'constants/filters';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Text,
    ToggleButton,
    ToggleButtonGroup,
    useBreakpoint,
    CaretLeftOutlined,
    Group,
    Button,
} from '@landr/maestro';
import { Trans } from '@lingui/macro';
import {
    KeyQuality,
    KeyRootAPI,
    toKeyRootApi,
    KeyRoot,
    KeySign,
    getNormalizedRootValue,
} from 'types/enums';
import { keyboardSorting } from 'utils/sort';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { PopoverBackButton, PopoverHeader } from '../Creator.styled';
import { Styled } from './KeyControl.styled';

interface ValueProps<T> {
    value: T;
    setValue: (
        value: T,
    ) => void | React.Dispatch<React.SetStateAction<KeyQuality>>;
}

interface KeysProps {
    value: string;
    setValue: (
        value: string,
    ) => void | React.Dispatch<React.SetStateAction<KeyQuality>>;
    keySign: KeySign;
    normalizedRootValue: KeyRoot | '';
}

const KeySignToggle = ({
    value,
    setValue,
}: ValueProps<KeySign>): JSX.Element => {
    // TODO: update ButtonGroup to allow onChange to accept a custom type https://mixgenius.atlassian.net/browse/MG-38120
    const handleChange: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: KeySign) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <ToggleButtonGroup value={value} onChange={handleChange}>
            <ToggleButton value={KeySign.Flat}>
                <Trans>Flat keys</Trans>
            </ToggleButton>
            <ToggleButton value={KeySign.Sharp}>
                <Trans>Sharp keys</Trans>
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

const Keys = ({
    keySign,
    setValue,
    normalizedRootValue,
}: KeysProps): JSX.Element => {
    const isMobile = useBreakpoint('md');

    const notesToMap = keySign === KeySign.Flat ? FlatKeyNotes : SharpKeyNotes;
    const notesToMapSorted = isMobile
        ? notesToMap
        : notesToMap.slice(0).sort(keyboardSorting);

    const handleChange: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: any) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <Styled.KeyWrapper>
            {notesToMapSorted.map((note) => (
                <ToggleButton
                    value={note}
                    key={note}
                    isRounded
                    isSelected={note === normalizedRootValue}
                    onChange={handleChange}
                >
                    {note}
                </ToggleButton>
            ))}
        </Styled.KeyWrapper>
    );
};

const KeyQualityToggle = ({
    value,
    setValue,
}: ValueProps<KeyQuality>): JSX.Element => {
    const handleClick: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: KeyQuality) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <Group gutter="sm">
            <Group.Item>
                <ToggleButton
                    isRounded
                    value={KeyQuality.Major}
                    onClick={handleClick}
                    isSelected={value === KeyQuality.Major}
                >
                    <Trans>Major</Trans>
                </ToggleButton>
            </Group.Item>
            <Group.Item>
                <ToggleButton
                    isRounded
                    value={KeyQuality.Minor}
                    onClick={handleClick}
                    isSelected={value === KeyQuality.Minor}
                >
                    <Trans>Minor</Trans>
                </ToggleButton>
            </Group.Item>
        </Group>
    );
};

export interface KeyControlProps {
    onClick: () => void;
}

export const KeyControl: React.FC<KeyControlProps> = ({
    onClick,
}: KeyControlProps): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { state, dispatch } = useCreatorContext();

    const numberOfSamplesInCreator = state.samplesInSession.size;

    const [keyRootNote, setKeyRootNote] = useState<KeyRootAPI>(state.keyRoot);
    const [keyQuality, setKeyQuality] = useState<KeyQuality>(state.keyQuality);
    const [keySign, setKeySign] = useState(state.keySign);
    const [normalizedRootValue, setNormalizedRootValue] = useState<KeyRoot>(
        KeyRoot.C,
    );

    const setTargetKey = useCallback(
        (root: KeyRootAPI, quality: KeyQuality): void => {
            dispatch({
                type: 'changeKey',
                payload: { root, quality, sign: keySign },
            });
        },
        [dispatch, keySign],
    );

    // TODO: re-implement once recommended key logic is implemented
    // const setRecommendedKey = useCallback(() => {
    //     setKeyRootNote(KeyRootAPI.C);
    //     setKeyQuality(KeyQuality.Major);
    //     setTargetKey(KeyRootAPI.C, KeyQuality.Major);
    // }, [setKeyRootNote, setKeyQuality, setTargetKey]);

    const handleApply = useCallback(() => {
        setTargetKey(keyRootNote, keyQuality);
        onClick();
    }, [
        keyQuality,
        keyRootNote,
        setTargetKey,
        numberOfSamplesInCreator,
        state.instrumentationSessionId,
        onClick,
    ]);

    useEffect(() => {
        setNormalizedRootValue(getNormalizedRootValue(keyRootNote, keySign));
    }, [keyRootNote, keySign]);

    const handleFocus = (): void => {
        containerRef?.current?.focus();
    };

    const handleKeyPress = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };

    return (
        <Styled.KeyControl
            onKeyDown={handleKeyPress}
            ref={containerRef}
            tabIndex={0}
        >
            <PopoverHeader>
                <PopoverBackButton
                    kind="minimal"
                    icon={CaretLeftOutlined}
                    onClick={onClick}
                >
                    <Trans>Back</Trans>
                </PopoverBackButton>
            </PopoverHeader>
            <Styled.InputWrapper onClick={handleFocus}>
                <KeySignToggle value={keySign} setValue={setKeySign} />
                <Styled.Title>
                    <Text size="md" contrast="low" fontWeight="bold">
                        <Trans>Root note</Trans>
                    </Text>
                </Styled.Title>
                <Keys
                    setValue={(value: string): void =>
                        setKeyRootNote(toKeyRootApi(value))
                    }
                    value={keyRootNote}
                    keySign={keySign}
                    normalizedRootValue={normalizedRootValue}
                />
                <Styled.Title>
                    <Text size="md" contrast="low" fontWeight="bold">
                        <Trans>Chord type</Trans>
                    </Text>
                </Styled.Title>
                <KeyQualityToggle value={keyQuality} setValue={setKeyQuality} />
            </Styled.InputWrapper>
            <Styled.Footer>
                <Button variant="primary" kind="link" onClick={handleApply}>
                    <Trans>Apply</Trans>
                </Button>
            </Styled.Footer>
        </Styled.KeyControl>
    );
};
